"use client"

import { NavLink } from "react-router-dom"
import { ChevronRight, type LucideIcon } from "lucide-react"
import { useSidebar } from "@/components/ui/sidebar"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"
import { Badge } from "@/components/ui/badge"

export function NavMain({
  items,
}: {
  items: {
    title: string
    url: string
    icon?: LucideIcon
    isActive?: boolean
    badge?: {
      count?: number;
      variant?: 'default' | 'destructive' | 'success' | 'warning';
      showDot?: boolean;
    };
    items?: {
      title: string
      url: string
      isActive?: boolean
      badge?: {
        count?: number;
        variant?: 'default' | 'destructive' | 'success' | 'warning';
        showDot?: boolean;
      };
    }[]
  }[]
}) {
  const { isMobile, state, setOpen } = useSidebar()
  
  return (
    <SidebarGroup>
      <SidebarGroupLabel>AKA Vault</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => (
          <Collapsible
            key={item.title}
            asChild
            defaultOpen={item.isActive}
            className="group/collapsible"
          >
            <SidebarMenuItem>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton 
                  tooltip={item.title}
                  onClick={(e) => {
                    // If sidebar is collapsed and item has sub-items, expand the sidebar to show sub-menu
                    if (state?.open === false && item.items && item.items.length > 0 && !isMobile) {
                      e.preventDefault()
                      setOpen?.(true)
                    }
                  }}
                >
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                  {item.badge && (
                    <div className="ml-auto flex items-center">
                      {item.badge.showDot ? (
                        <div className="h-2 w-2 rounded-full bg-red-500"></div>
                      ) : item.badge.count !== undefined && item.badge.count > 0 ? (
                        <Badge variant={item.badge.variant || "default"} className="ml-2">
                          {item.badge.count}
                        </Badge>
                      ) : null}
                    </div>
                  )}
                  <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarMenuSub>
                  {item.items?.map((subItem) => (
                    <SidebarMenuSubItem key={subItem.title}>
                      <SidebarMenuSubButton isActive={subItem.isActive} asChild>
                        <NavLink to={subItem.url} className="flex items-center justify-between w-full">
                          <span>{subItem.title}</span>
                          {subItem.badge && (
                            <div className="ml-auto flex items-center">
                              {subItem.badge.showDot ? (
                                <div className="h-2 w-2 rounded-full bg-red-500"></div>
                              ) : subItem.badge.count !== undefined && subItem.badge.count > 0 ? (
                                <Badge variant={subItem.badge.variant || "default"} className="ml-2">
                                  {subItem.badge.count}
                                </Badge>
                              ) : null}
                            </div>
                          )}
                        </NavLink>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  )
}
